import { api, type ApiResponse } from '../client';
import apiClient from '../client';

export interface Employee {
  uuid: string;
  full_name: string;
  email?: string;
}

class EmployeesSearchService {
  private baseUrl = '/admin/employees';
  private employeeCache: Map<string, string> = new Map();

  /**
   * Search employees/users by phrase (name, email, etc.)
   * @param phrase Search query
   * @param limit Maximum number of results to return
   * @returns Promise with list of matching employees
   */
  async searchEmployees(phrase: string = '', limit: number = 10): Promise<Array<{uuid: string, name: string}>> {
    try {
      // Require phrase to be at least 2 characters
      if (!phrase || phrase.trim().length < 2) {
        return [];
      }
      
      const params = new URLSearchParams();
      params.append('phrase', phrase.trim());
      params.append('per_page', limit.toString());
      params.append('page', '1');

      const axiosResponse = await apiClient.get(`${this.baseUrl}?${params.toString()}`);
      
      const serverResponse = axiosResponse.data;
      
      if (serverResponse && serverResponse.success && serverResponse.data && Array.isArray(serverResponse.data)) {
        const results = serverResponse.data.map((employee: Employee) => {
          // Cache the employee name for later retrieval
          this.employeeCache.set(employee.uuid, employee.full_name);
          return {
            uuid: employee.uuid,
            name: employee.full_name
          };
        });
        return results;
      }
      
      return [];
    } catch (error: any) {
      console.error('Error searching employees:', error);
      return [];
    }
  }

  /**
   * Get employee name by UUID (uses cache first, then searches if needed)
   * @param uuid Employee UUID
   * @returns Promise with employee name or null
   */
  async getEmployeeName(uuid: string): Promise<string | null> {
    // First check cache
    if (this.employeeCache.has(uuid)) {
      return this.employeeCache.get(uuid) || null;
    }

    // If not in cache, try to search for it by searching with a very specific query
    // This is a fallback - ideally the name should be cached from selection
    try {
      // Search with UUID as phrase (though this might not work, it's a fallback)
      const results = await this.searchEmployees(uuid, 1);
      const found = results.find(emp => emp.uuid === uuid);
      if (found) {
        return found.name;
      }
      
      return null;
    } catch (error: any) {
      console.error('Error getting employee name:', error);
      return null;
    }
  }

  /**
   * Cache an employee name for later retrieval
   * @param uuid Employee UUID
   * @param name Employee name
   */
  cacheEmployeeName(uuid: string, name: string): void {
    this.employeeCache.set(uuid, name);
  }
}

export const employeesSearchService = new EmployeesSearchService();
export default employeesSearchService;

